import { useMemo } from 'react';
import { AnimatePresence, motion, useReducedMotion } from 'motion/react';
import { useTranslation } from 'react-i18next';
import { useMoney } from '../../../lib/money/useMoney.js';
import { Icon } from '../../../components/ui/icon.js';
import { cappedMultiplierBp, multiplierBp, nextTileSurvivalPct, payoutMinor } from './multiplier.js';

const UPCOMING_STEPS = 4;

interface Props {
  bet: bigint;
  minesCount: number;
  /** Safe tiles revealed so far this round (0 when idle/settled). */
  revealedSafe: number;
  active: boolean;
  currency?: string;
  /** Authoritative bp for the NEXT step from the server; client mirror covers the rest. */
  nextBpStr?: string;
  /** The server's cashout clamp for THIS agent's currency, from the mines config. */
  maxWinMinor: bigint;
}

function survivalTone(pct: number): string {
  if (pct >= 75) return 'var(--success)';
  if (pct >= 50) return 'var(--warning)';
  return 'var(--danger)';
}

/**
 * The "climb" above the board: the next multiplier steps you're chasing, with
 * the very next step big and bright (×mult + actual payout), later steps fading
 * into the distance. While a round is live a survival meter underneath counts
 * the odds the next tile is safe — draining and reddening as the board thins.
 */
export function MinesClimbStrip({ bet, minesCount, revealedSafe, active, currency, nextBpStr, maxWinMinor }: Props) {
  const { t } = useTranslation('originals');
  const { fromMinor } = useMoney(currency);
  const reduced = useReducedMotion() ?? false;

  const safeTotal = 25 - minesCount;
  const steps = useMemo(() => {
    const out: { k: number; bp: bigint }[] = [];
    for (let i = 1; i <= UPCOMING_STEPS; i++) {
      const k = revealedSafe + i;
      if (k > safeTotal) break;
      // Server value wins for the immediate next step when a round is live.
      const bp = i === 1 && active && nextBpStr ? BigInt(nextBpStr) : multiplierBp(k, minesCount);
      out.push({ k, bp });
    }
    return out;
  }, [revealedSafe, safeTotal, minesCount, active, nextBpStr]);

  if (steps.length === 0) return null;

  const survival = nextTileSurvivalPct(minesCount, revealedSafe);
  const remaining = 25 - revealedSafe;
  const tone = survivalTone(survival);

  return (
    // The site's own surface behind the steps and odds: the painted stage is brightest right
    // where the strip sits.
    <div className="mx-auto mb-3 max-w-[460px] rounded-2xl bg-[color-mix(in_oklab,var(--bg-raised)_85%,transparent)] p-2 backdrop-blur-[2px]">
      <div className="flex items-stretch gap-1.5 overflow-hidden">
        <AnimatePresence initial={false} mode="popLayout">
          {steps.map(({ k, bp }, i) => {
            const next = i === 0;
            const mult = Number(cappedMultiplierBp(bet, bp, maxWinMinor)) / 10000;
            return (
              <motion.div
                key={k}
                layout={!reduced}
                initial={reduced ? false : { opacity: 0, x: 24, scale: 0.9 }}
                animate={{ opacity: 1, x: 0, scale: 1 }}
                exit={reduced ? { opacity: 0 } : { opacity: 0, x: -28, scale: 0.85 }}
                transition={{ type: 'spring', stiffness: 420, damping: 30 }}
                className={[
                  'flex min-w-0 flex-col items-center justify-center rounded-xl border px-2 py-1.5',
                  next
                    ? 'flex-[1.6] border-[var(--primary)] bg-[var(--primary)]/15 shadow-[0_0_16px_var(--primary-glow,var(--primary))]'
                    : 'flex-1 border-[var(--border)] bg-[var(--bg-elevated)]',
                ].join(' ')}
                style={next ? undefined : { opacity: Math.max(0.35, 0.75 - i * 0.15) }}
              >
                <span className="flex items-center gap-1 text-[10px] font-bold uppercase tracking-wide text-[var(--text-muted)]">
                  <Icon name="gem" size={10} className={next ? 'text-[var(--primary)]' : ''} />
                  {k}
                </span>
                <span
                  className={[
                    'bf-tnum font-extrabold leading-tight',
                    next ? 'text-[16px] text-[var(--primary)]' : 'text-[12.5px] text-[var(--text-secondary)]',
                  ].join(' ')}
                >
                  ×{mult.toFixed(2)}
                </span>
                {next && (
                  <span className="bf-tnum text-[11px] font-bold leading-tight text-[var(--success)]">
                    {fromMinor(payoutMinor(bet, bp, maxWinMinor))}
                  </span>
                )}
              </motion.div>
            );
          })}
        </AnimatePresence>
      </div>

      {/* Kept in the layout between rounds, only hidden, so the board under it never jumps. */}
      <div className={['mt-2 flex flex-col gap-1', active ? '' : 'invisible'].join(' ')} aria-hidden={!active}>
        <div className="h-1.5 overflow-hidden rounded-full bg-[var(--bg-elevated)]">
          <motion.div
            className="h-full rounded-full"
            animate={{ width: `${survival}%`, backgroundColor: tone }}
            transition={reduced ? { duration: 0 } : { type: 'spring', stiffness: 200, damping: 26 }}
            style={{ boxShadow: `0 0 8px ${tone}` }}
          />
        </div>
        <div className="flex items-center justify-between gap-2 text-[11px] font-semibold">
          <span className="bf-tnum" style={{ color: tone }}>
            {t('mines.odds.survive', { pct: survival })}
          </span>
          <span className="bf-tnum flex items-center gap-1 text-[var(--text-muted)]">
            <Icon name="bomb" size={11} className="text-[var(--danger)]" />
            {t('mines.odds.minesLeft', { mines: minesCount, tiles: remaining })}
          </span>
        </div>
      </div>
    </div>
  );
}
